import { NodeType } from '../Types/TaskStatus';
import { NodeMetadataRegistry } from '../Runtime/NodeMetadata';
/**
 * 内置属性类型常量
 */
export const PropertyType = {
    /** 字符串 */
    String: 'string',
    /** 数值 */
    Number: 'number',
    /** 布尔值 */
    Boolean: 'boolean',
    /** 选择框 */
    Select: 'select',
    /** 黑板变量引用 */
    Blackboard: 'blackboard',
    /** 代码编辑器 */
    Code: 'code',
    /** 变量引用 */
    Variable: 'variable',
    /** 资产引用 */
    Asset: 'asset'
};
/**
 * 节点模板库
 */
export class NodeTemplates {
    /**
     * 获取所有节点模板
     */
    static getAllTemplates() {
        const allMetadata = NodeMetadataRegistry.getAllMetadata();
        return allMetadata.map(metadata => this.convertMetadataToTemplate(metadata));
    }
    /**
     * 根据类型和子类型获取模板
     */
    static getTemplate(type, subType) {
        return this.getAllTemplates().find(t => {
            if (t.type !== type)
                return false;
            const config = t.defaultConfig;
            switch (type) {
                case NodeType.Composite:
                    return config.compositeType === subType;
                case NodeType.Decorator:
                    return config.decoratorType === subType;
                case NodeType.Action:
                    return config.actionType === subType;
                case NodeType.Condition:
                    return config.conditionType === subType;
                default:
                    return false;
            }
        });
    }
    /**
     * 将NodeMetadata转换为NodeTemplate
     */
    static convertMetadataToTemplate(metadata) {
        const properties = this.convertConfigSchemaToProperties(metadata.configSchema || {});
        const defaultConfig = {
            nodeType: this.nodeTypeToString(metadata.nodeType)
        };
        switch (metadata.nodeType) {
            case NodeType.Composite:
                defaultConfig.compositeType = metadata.implementationType;
                break;
            case NodeType.Decorator:
                defaultConfig.decoratorType = metadata.implementationType;
                break;
            case NodeType.Action:
                defaultConfig.actionType = metadata.implementationType;
                break;
            case NodeType.Condition:
                defaultConfig.conditionType = metadata.implementationType;
                break;
        }
        if (metadata.configSchema) {
            for (const [key, field] of Object.entries(metadata.configSchema)) {
                const fieldDef = field;
                if (fieldDef.default !== undefined) {
                    defaultConfig[key] = fieldDef.default;
                }
            }
        }
        // 根据节点类型生成默认颜色和图标
        const { icon, color } = this.getIconAndColorByType(metadata.nodeType, metadata.category || '');
        return {
            type: metadata.nodeType,
            displayName: metadata.displayName,
            category: metadata.category || this.getCategoryByNodeType(metadata.nodeType),
            description: metadata.description || '',
            className: metadata.implementationType,
            icon,
            color,
            defaultConfig,
            properties
        };
    }
    /**
     * 将ConfigSchema转换为PropertyDefinition数组
     */
    static convertConfigSchemaToProperties(configSchema) {
        const properties = [];
        for (const [name, field] of Object.entries(configSchema)) {
            const property = {
                name,
                type: this.mapFieldTypeToPropertyType(field),
                label: name
            };
            if (field.description !== undefined) {
                property.description = field.description;
            }
            if (field.default !== undefined) {
                property.defaultValue = field.default;
            }
            if (field.min !== undefined) {
                property.min = field.min;
            }
            if (field.max !== undefined) {
                property.max = field.max;
            }
            if (field.allowMultipleConnections !== undefined) {
                property.allowMultipleConnections = field.allowMultipleConnections;
            }
            if (field.options) {
                property.options = field.options.map(opt => ({
                    label: opt,
                    value: opt
                }));
            }
            if (field.supportBinding) {
                property.renderConfig = {
                    component: 'BindableInput',
                    props: {
                        supportBinding: true
                    }
                };
            }
            properties.push(property);
        }
        return properties;
    }
    /**
     * 映射字段类型到属性类型
     */
    static mapFieldTypeToPropertyType(field) {
        if (field.options && field.options.length > 0) {
            return PropertyType.Select;
        }
        switch (field.type) {
            case 'string':
                return PropertyType.String;
            case 'number':
                return PropertyType.Number;
            case 'boolean':
                return PropertyType.Boolean;
            case 'array':
            case 'object':
            default:
                return PropertyType.String;
        }
    }
    /**
     * NodeType转字符串
     */
    static nodeTypeToString(nodeType) {
        switch (nodeType) {
            case NodeType.Composite:
                return 'composite';
            case NodeType.Decorator:
                return 'decorator';
            case NodeType.Action:
                return 'action';
            case NodeType.Condition:
                return 'condition';
            default:
                return 'unknown';
        }
    }
    /**
     * 根据NodeType获取默认分类
     */
    static getCategoryByNodeType(nodeType) {
        switch (nodeType) {
            case NodeType.Composite:
                return '组合';
            case NodeType.Decorator:
                return '装饰器';
            case NodeType.Action:
                return '动作';
            case NodeType.Condition:
                return '条件';
            default:
                return '其他';
        }
    }
    /**
     * 根据节点类型获取默认图标和颜色
     */
    static getIconAndColorByType(nodeType, _category) {
        // 根据节点类型设置默认值
        switch (nodeType) {
            case NodeType.Composite:
                return { icon: 'GitBranch', color: '#1976d2' }; // 蓝色
            case NodeType.Decorator:
                return { icon: 'Settings', color: '#fb8c00' }; // 橙色
            case NodeType.Action:
                return { icon: 'Play', color: '#388e3c' }; // 绿色
            case NodeType.Condition:
                return { icon: 'HelpCircle', color: '#d32f2f' }; // 红色
            default:
                return { icon: 'Circle', color: '#757575' }; // 灰色
        }
    }
}
//# sourceMappingURL=NodeTemplates.js.map